<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { ToastPosition, Toast, useToastStore } from "@/stores/toast";

const toastStore = useToastStore();

const toastsByPosition = computed(() => {
	const positions: Record<ToastPosition, Toast[]> = {
		"top-right": [],
		"top-left": [],
		"bottom-right": [],
		"bottom-left": [],
		"top-center": [],
		"bottom-center": [],
	};

	toastStore.toasts.forEach((toast) => {
		const position = toast.position || "bottom-right";
		positions[position].push(toast);
	});

	return positions;
});

function getIconForType(type: Toast["type"]) {
	switch (type) {
		case "info":
			return "mdi-information-outline";
		case "warning":
			return "mdi-alert-outline";
		case "error":
			return "mdi-alert-circle-outline";
		case "loading":
			return "fa-regular fa-spinner-third spin";
		default:
			return "mdi-information-outline";
	}
}

function getColorForType(type: Toast["type"]) {
	switch (type) {
		case "info":
			return "primary";
		case "warning":
			return "warning";
		case "error":
			return "error";
		case "loading":
			return "grey";
		default:
			return "primary";
	}
}

const leavingToasts = ref(new Set<string>());

function dismissToast(toast: Toast) {
	if (!toast.dismissible) return;

	leavingToasts.value.add(toast.id);
	setTimeout(() => {
		toastStore.remove(toast.id);
		leavingToasts.value.delete(toast.id);
	}, 300);
}

onMounted(() => toastStore.clear());
</script>

<template>
	<div
		v-for="(position, positionKey) in toastsByPosition"
		:key="positionKey"
		:class="['toast-container', `position-${positionKey}`]"
	>
		<transition-group name="toast">
			<div
				v-for="toast in position"
				:key="toast.id"
				:class="[
					'toast',
					`toast-${toast.type}`,
					{ 'toast-leaving': leavingToasts.has(toast.id) },
					{ 'toast-clickable': !!toast.onClick },
				]"
				@click="toast.onClick?.()"
			>
				<div class="toast-icon">
					<v-icon
						:icon="getIconForType(toast.type)"
						:color="getColorForType(toast.type)"
					/>
				</div>
				<div class="toast-content">
					<div v-if="toast.title" class="toast-title">{{ toast.title }}</div>
					<div class="toast-message" v-html="toast.message"></div>
				</div>
				<div v-if="toast.dismissible" class="toast-close">
					<v-icon
						icon="mdi-close"
						size="small"
						@click.stop="dismissToast(toast)"
					/>
				</div>
			</div>
		</transition-group>
	</div>
</template>

<style scoped>
.toast-container {
	position: fixed;
	display: flex;
	flex-direction: column;
	gap: 10px;
	z-index: 9999;
	max-width: 350px;
	max-height: 100vh;
	overflow-y: auto;
	overflow-x: hidden;
	padding: 16px;
	pointer-events: none;
}

/* Position variations */
.position-top-right {
	top: 0;
	right: 0;
}

.position-top-left {
	top: 0;
	left: 0;
}

.position-bottom-right {
	bottom: 0;
	right: 0;
}

.position-bottom-left {
	bottom: 0;
	left: 0;
}

.position-top-center {
	top: 0;
	left: 50%;
	transform: translateX(-50%);
}

.position-bottom-center {
	bottom: 0;
	left: 50%;
	transform: translateX(-50%);
}

.toast {
	display: flex;
	align-items: flex-start;
	background: white;
	border-radius: 12px;
	padding: 12px 16px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	min-width: 300px;
	max-width: 100%;
	pointer-events: auto; /* Make toast clickable */
	cursor: pointer;
	position: relative;
	overflow: hidden;
}

.toast::after {
	content: "";
	position: absolute;
	bottom: 0;
	left: 0;
	height: 3px;
	width: 100%;
	background-color: var(--v-theme-background);
}

.toast-info::after {
	background-color: var(--v-theme-primary);
}

.toast-warning::after {
	background-color: var(--v-theme-warning);
}

.toast-error::after {
	background-color: var(--v-theme-error);
}

.toast-loading::after {
	background-color: var(--v-theme-grey);
}

.toast-icon {
	margin-right: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.toast-content {
	flex: 1;
	margin-right: 8px;
}

.toast-title {
	font-weight: 600;
	margin-bottom: 4px;
}

.toast-message {
	color: rgba(0, 0, 0, 0.7);
	font-size: 0.95em;
	line-height: 1.4;
	/* Wrap rather than clip: progress messages carry counts and periods that were
	   silently cut off by a fixed max-width. */
	overflow-wrap: anywhere;
}

/* Only toasts that actually do something on click look clickable. */
.toast {
	cursor: default;
}

.toast-clickable {
	cursor: pointer;
}

.toast-close {
	align-self: flex-start;
	opacity: 0.5;
	transition: opacity 0.2s;
}

.toast-close:hover {
	opacity: 1;
}

/* Animation */
.toast-enter-active,
.toast-leave-active {
	transition: all 0.5s ease;
}

.toast-enter-from {
	opacity: 0;
	transform: translateY(30px);
}

.toast-leave-to {
	opacity: 0;
	transform: translateX(100%);
}

/* Different entrance for different positions */
.position-top-left .toast-enter-from,
.position-bottom-left .toast-enter-from {
	transform: translateX(-30px);
}

.position-top-right .toast-enter-from,
.position-bottom-right .toast-enter-from {
	transform: translateX(30px);
}

.position-top-center .toast-enter-from {
	transform: translateY(-30px);
}

.position-bottom-center .toast-enter-from {
	transform: translateY(30px);
}

/* Different exit animations based on position */
.position-top-left .toast-leave-to,
.position-bottom-left .toast-leave-to {
	transform: translateX(-100%);
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.spin {
	animation-name: spin;
	animation-duration: 500ms;
	animation-iteration-count: infinite;
	animation-timing-function: linear;
}
</style>
